import React, { useEffect, useState } from "react";
import { api, useAuth } from "../../lib/auth";
import { Send, RefreshCw } from "lucide-react";

export default function Messaging() {
  const { user } = useAuth();
  // Phase 10 F-AS-04 · explicit async states; error must be
  // distinguishable from empty.  Backend contract is unchanged.
  const [feed, setFeed] = useState({ status: "loading", items: [] });
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setFeed((f) => ({ status: "loading", items: f.items }));
    api
      .get("/portal/messages")
      .then((r) => setFeed({ status: "ok", items: r.data.items || [] }))
      .catch(() => setFeed({ status: "error", items: [] }));
  };
  useEffect(() => {
    load();
  }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/portal/messages", { body, thread_id: "care-team" });
      setBody("");
      load();
    } finally {
      setBusy(false);
    }
  };

  const list = feed.items.slice().reverse();

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#0B132B]">Messaging</h1>
      <p className="text-[#4B5563]">Private, secure conversations with your care team and community.</p>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* BTC3 Phase 6 Truthful UI · §3 — the previous conversation
            sidebar carried a hand-wired three-row contact list plus a
            fabricated member count.  The Messaging backend only
            persists a single `care-team` thread today, so the sidebar
            now reflects that single thread honestly. */}
        <div className="bg-white rounded-2xl border border-gray-100 p-3" data-testid="messaging-threads">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#F8F9FB]">
            <div className="w-10 h-10 rounded-full text-white flex items-center justify-center text-xs font-bold" style={{ backgroundColor: "#23803F" }} aria-hidden="true">C</div>
            <div>
              <div className="text-sm font-semibold text-[#0B132B]">Care Team</div>
              <div className="text-xs text-[#4B5563]">Active thread</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 flex flex-col h-[560px]">
          <div className="p-4 border-b border-gray-100">
            <div className="font-semibold text-[#0B132B]">Care Team</div>
          </div>
          {/* Phase 10 F-AS-04 / F-F-03 · explicit loading / error /
              empty / available with aria-live for incremental
              announcements. */}
          <div
            className="flex-1 overflow-y-auto p-4 space-y-3"
            aria-live="polite"
            aria-busy={feed.status === "loading"}
          >
            {feed.status === "loading" && list.length === 0 && (
              <div
                data-testid="messaging-loading"
                role="status"
                className="text-center text-sm text-[#4B5563] py-12"
              >
                Loading your conversation…
              </div>
            )}
            {feed.status === "error" && (
              <div
                data-testid="messaging-error"
                role="alert"
                className="text-center text-sm text-[#B00020] py-12"
              >
                Couldn’t load your messages.{" "}
                <button
                  type="button"
                  onClick={load}
                  data-testid="messaging-retry"
                  className="underline inline-flex items-center gap-1 font-semibold"
                >
                  <RefreshCw className="w-3 h-3" aria-hidden="true" /> Retry
                </button>
              </div>
            )}
            {feed.status === "ok" && list.length === 0 && (
              <div
                data-testid="messaging-empty"
                className="text-center text-sm text-[#4B5563] py-12"
              >
                No messages yet. Start a conversation.
              </div>
            )}
            {list.map((m) => {
              const mine = m.from_user_id === user.id;
              return (
                <div key={m.id} className={`max-w-[75%] ${mine ? "ml-auto" : ""}`}>
                  <div
                    className={`rounded-2xl px-4 py-2.5 text-sm ${
                      mine ? "bg-[#23803F] text-white rounded-tr-sm" : "bg-[#F8F9FB] text-[#0B132B] rounded-tl-sm"
                    }`}
                  >
                    {m.body}
                  </div>
                  <div className={`text-[10px] text-gray-400 mt-1 ${mine ? "text-right" : ""}`}>
                    {new Date(m.created_at).toLocaleTimeString()}
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={send} className="p-3 border-t border-gray-100 flex gap-2" aria-label="Send a message to your care team">
            <label htmlFor="messaging-composer-input" className="sr-only">
              Message body
            </label>
            <input
              id="messaging-composer-input"
              data-testid="messaging-composer-input"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a message..."
              aria-label="Message body"
              className="flex-1 px-4 py-2.5 rounded-full bg-[#F8F9FB] border border-gray-100 focus:outline-none focus:border-[#23803F] focus:ring-2 focus:ring-[#23803F]/30"
            />
            <button
              type="submit"
              disabled={busy}
              aria-label="Send message"
              data-testid="messaging-send-button"
              className="btc-btn-primary px-5 py-2.5 disabled:opacity-50"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
